<template>
    <Modal
        :open="open"
        :additional-class-name="['TprmModal', additionalClassName, `TprmModal--${size}`, fullScreen && 'TprmModal--fullScreen']"
        :allow-close="allowClose"
        :close-on-backdrop="closeOnBackdrop"
        @close="$emit('close')"
    >
        <template #header-slot>
            <slot name="header-slot">
                <div class="TprmModal__headerContent">
                    <slot name="header-title-container">
                        <div class="TprmModal__headerTitleContainer">
                            <div class="TprmModal__headerTitle">
                                {{ title }}
                            </div>
                            <div class="TprmModal__headerSubtitle">
                                {{ subtitle }}
                            </div>
                        </div>
                    </slot>
                    <slot v-if="showCloseIcon" name="header-close">
                        <NewButton
                            variant="flat"
                            color="secondary"
                            size="icon-sm"
                            @click="$emit('close')"
                        >
                            <EidIcon :icon-src="faXmark" />
                        </NewButton>
                    </slot>
                </div>
            </slot>
        </template>
        <slot />
        <template #footer>
            <slot name="footer" />
        </template>
    </Modal>
</template>

<style lang="scss">
.TprmModal {
    .Modal__header {
        margin: 0;
        border: none;
        min-height: initial;
    }

    .TprmModal__headerContent {
        display: flex;
        justify-content: space-between;
        gap: 5px;
    }

    .TprmModal__headerTitle {
        font-size: 18px !important;
        line-height: 1.2em;
        font-weight: 700;
    }

    .Modal__headerContent {
        text-align: center;
        font-size: 18px !important;
        padding: 0 !important;
        margin: 0 0 1em !important;
        font-weight: 700;
        line-height: 1.2em;
    }

    .Modal__content {
        padding: 0 !important;
        margin: 0;
        font-size: 14px !important;
    }

    .Modal__body {
        display: flex;
        flex-direction: column;
        gap: 21px;
        border-radius: 8.75px;
        padding: 21px;
    }

    .Modal__footer {
        justify-content: right;
        margin: 0;
        margin: 0;
        padding: 0 !important;
        gap: 10.5px;
    }

    &--default {
        .Modal__body {
            width: 450px;
        }
    }

    &--md {
        .Modal__body {
            width: 600px;
        }
    }

    &--lg {
        .Modal__body {
            width: 750px;
        }
    }

    &--xl {
        .Modal__body {
            width: 850px;
        }
    }

    &--fullScreen {
        .Modal__body {
            max-width: 95vw;
            max-height: 95vh;
            height: 100%;
            width: 100%;
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faXmark } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { Modal } from '@evidentid/dashboard-commons/components/Modal';

    import NewButton from '@/components/new-button/NewButton.vue';

    // PRODUCT-26110 QA fixes4: declares the `close` event this component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'close' but it is
    // neither declared in the emits option nor as an 'onClose' prop" every time it fires.
    export default defineComponent({
        name: 'TprmModal',
        components: {
            Modal,
            NewButton,
            EidIcon,
        },
        props: {
            title: {
                type: String,
                default: '',
            },
            subtitle: {
                type: String,
                default: '',
            },
            size: {
                type: String as PropType<'default' | 'md' | 'lg' | 'xl'>,
                default: 'md',
            },
            additionalClassName: {
                type: String,
                default: '',
            },
            allowClose: {
                type: Boolean,
                default: false,
            },
            closeOnBackdrop: {
                type: Boolean,
                default: false,
            },
            open: {
                type: Boolean,
                default: false,
            },
            showCloseIcon: {
                type: Boolean,
                default: true,
            },
            fullScreen: {
                type: Boolean,
                default: false,
            },
        },
        emits: [ 'close' ],
        data() {
            return {
                faXmark,
            };
        },
    });
</script>
